import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Alert,
  Button,
  Divider,
  Loader,
  Modal,
  Select,
  Space,
  Text,
  Title,
  SegmentedControl,
} from "@mantine/core";
import { DateRangePicker } from "@mantine/dates";
import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import { TripTable } from "./trip-table";
import { TripTimeline } from "./trip-drawer-details";
import {
  getWeekOldDate,
  useHandleQueryResult,
  useResetListingState,
} from "../../components/listings/hooks";
import { useTripBuildQuery } from "../../components/listings/hooks/use-listing-query";
import { DateRangeQuickPills } from "../../components/shared/date-range-quick-pills";
import {
  dateParam,
  enumParam,
  stringParam,
  useUrlOverlayState,
  useUrlState,
} from "../../hooks/useUrlState";
import { GoogleMap, Polyline, InfoWindow, useLoadScript, Marker } from "@react-google-maps/api";
import { MapSearch, ChevronDown, ChevronUp } from "tabler-icons-react";
import { useMediaQuery } from "@mantine/hooks";

import { Trip } from "@captiva/shared-types";

export interface TripsProps {
  auth: {
    claims: {
      admin?: boolean;
      companyId?: string;
      user_id?: string;
    };
  } | null | undefined;
  companyId: string;
}

const GOOGLE_MAPS_LIBRARIES: ("places" | "drawing" | "geometry" | "visualization")[] = ["places"];

export function Trips({ auth, companyId }: TripsProps) {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries: GOOGLE_MAPS_LIBRARIES,
  });
  const defaultFromDate = useMemo(() => getWeekOldDate(1), []);
  const defaultToDate = useMemo(() => new Date(), []);
  const urlSchema = useMemo(() => ({
    from: dateParam(null),
    to: dateParam(null),
    status: enumParam([
      "PLANNED",
      "ALLOCATED",
      "PRE_INSPECTED",
      "IN_PROGRESS",
      "ARRIVED",
      "COMPLETED",
    ] as const, null),
    vehicle: stringParam(null),
    driver: stringParam(null),
    tripId: stringParam(null),
    mode: enumParam(["view"] as const, null),
  }), []);
  const [urlState, setUrlState] = useUrlState(urlSchema);
  const {
    selectedId: selectedTripId,
    mode,
    openOverlay,
    closeOverlay,
  } = useUrlOverlayState({
    idKey: "tripId",
    modeKey: "mode",
    defaultMode: "view",
    state: urlState,
    setState: setUrlState,
  });
  const hasExplicitDateRange = Boolean(urlState.from || urlState.to);
  const dates = useMemo<[Date | null, Date | null]>(() => {
    if (hasExplicitDateRange) {
      return [urlState.from, urlState.to];
    }

    return [defaultFromDate, defaultToDate];
  }, [defaultFromDate, defaultToDate, hasExplicitDateRange, urlState.from, urlState.to]);
  const dateRangeKey = `${dates[0] instanceof Date ? dates[0].toISOString() : ""}|${dates[1] instanceof Date ? dates[1].toISOString() : ""}`;
  const stableDates = useMemo(() => dates, [dateRangeKey]);
  const hasValidDateRange = Boolean(
    stableDates[0] && (!stableDates[1] || stableDates[0] <= stableDates[1])
  );
  const status = urlState.status || null;
  const selectedVehicle = urlState.vehicle || null;
  const selectedDriver = urlState.driver || null;
  const filterState = useMemo(() => ({
    status,
    selectedVehicle,
    selectedDriver,
  }), [selectedDriver, selectedVehicle, status]);
  const filterStateKey = useMemo(() => JSON.stringify(filterState), [filterState]);

  const isMobile = useMediaQuery("(max-width: 768px)");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [doneInitialFetch, setInitialFetched] = useState(false);
  const [lastVisibleDoc, setLastVisibleDoc] = useState<any>();
  const [paginationDone, setPaginationDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [vehicleOptions, setVehicleOptions] = useState<{ value: string; label: string }[]>([]);
  const [driverOptions, setDriverOptions] = useState<{ value: string; label: string }[]>([]);

  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [hoveredTrip, setHoveredTrip] = useState<Trip | null>(null);
  const [mapCenter, setMapCenter] = useState({ lat: 0, lng: 0 });
  const [selectedMapTrip, setSelectedMapTrip] = useState<Trip | null>(null);

  const [colors, setColors] = useState<string[]>([]);

  const [pageSize, setPageSize] = useState<number>(20);

  const handlePageSizeChange = (newPageSize: number) => {
    if (newPageSize === pageSize) return;
    setPageSize(newPageSize);
    reset();
  };

  const buildQuery = useTripBuildQuery({
    db,
    companyId,
    pageSize,
    collectionName: "trips",
  });

  const handleResults = useHandleQueryResult<Trip>({
    pageSize,
    setDocuments: setTrips,
    setLastVisibleDoc,
    setPaginationDone,
  });

  const reset = useResetListingState<Trip>({
    setDocuments: setTrips,
    setInitialFetched,
    setLastVisibleDoc,
    setPaginationDone,
  });

  // Effect to reset data when NON-DATE filters or companyId change
  useEffect(() => {
    if (companyId) {
      reset();
    }
  }, [companyId, filterState, filterStateKey, reset]);

  // Effect to handle changes in the DATE filter
  useEffect(() => {
    if (companyId) {
      if (hasValidDateRange) {
        // A date range is being selected or has changed.
        reset();
      }
    }
  }, [companyId, dateRangeKey, hasValidDateRange, reset, stableDates]);

  useEffect(() => {
    if (companyId) {
      const fetchVehicles = async () => {
        const vehiclesCollection = collection(db, "fleet");
        const q = query(vehiclesCollection, where("companyId", "==", companyId));
        const querySnapshot = await getDocs(q);
        const options = querySnapshot.docs.map((doc) => ({
          value: doc.id,
          label: doc.data().name || `Vehicle ${doc.id.substring(0, 5)}`,
        })).sort((a, b) => a.label.localeCompare(b.label));
        setVehicleOptions(options);
      };
      fetchVehicles();
    } else {
      setVehicleOptions([]);
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId) {
      const fetchDrivers = async () => {
        const fleetOperatorsCollection = collection(db, "fleetOperators");
        const q = query(
          fleetOperatorsCollection,
          where("companyId", "==", companyId)
        );
        const querySnapshot = await getDocs(q);
        const options = querySnapshot.docs.map((doc) => ({
          value: doc.id,
          label:
            doc.data().nickName ||
            doc.data().fullName ||
            `Driver ${doc.id.substring(0, 5)}`,
        })).sort((a, b) => a.label.localeCompare(b.label));
        setDriverOptions(options);
      };
      fetchDrivers();
    } else {
      setDriverOptions([]);
    }
  }, [companyId]);

  const loadMoreEntries = useCallback(
    async (isInitialLoad = false) => {
      setLoading(true);

      const q = buildQuery(isInitialLoad ? null : lastVisibleDoc, {
        dates: stableDates,
        status,
        selectedVehicle,
        selectedDriver,
      });
      const documentSnapshots = await getDocs(q);

      handleResults(documentSnapshots, isInitialLoad);

      if (isInitialLoad) {
        setInitialFetched(true);
      }
      setLoading(false);
    },
    [
      lastVisibleDoc,
      status,
      selectedVehicle,
      selectedDriver,
      buildQuery,
      handleResults,
      filterState,
      stableDates,
    ]
  );

  const updateDateRange = (pickupDate: string | number | Date | null | undefined) => {
    if (pickupDate) {
      const startDate = new Date(pickupDate);
      const endDate = new Date(startDate);
      endDate.setHours(23, 59, 59, 999); // Set end of the day
      setUrlState({ from: startDate, to: endDate }, { replace: false });
    } else {
      setUrlState({ from: null, to: null }, { replace: false });
    }
    reset();
  }

  useEffect(() => {
    if (companyId && hasValidDateRange && !loading && !doneInitialFetch) {
      loadMoreEntries(true);
    }
  }, [
    companyId,
    hasValidDateRange,
    loading,
    doneInitialFetch,
    loadMoreEntries,
    dateRangeKey,
    filterStateKey,
    stableDates,
  ]);

  const handleDateRangeChange = (range: [Date | null, Date | null]) => {
    const [fromDate, toDate] = range || [];

    setUrlState({
      from: fromDate || null,
      to: toDate || null,
    }, { replace: true });
  };

  const handleFilterChange = (key: string, value: string | null) => {
    setUrlState({ [key]: value || null }, { replace: true });
  };

  useEffect(() => {
    if (trips.length > 0 && trips[0].locationSnapshots && trips[0].locationSnapshots.length > 0) {
      const firstSnapshot = trips[0].locationSnapshots[0];
      if (firstSnapshot.lat != null && firstSnapshot.lon != null) {
        setMapCenter({ lat: firstSnapshot.lat, lng: firstSnapshot.lon });
      }
    }
  }, [trips]);

  useEffect(() => {
    if (trips.length > 0) {
      const generateBrightColors = (count: number) => {
        const colorsList: string[] = [];
        for (let i = 0; i < count; i++) {
          const hue = Math.floor(Math.random() * 360); // Random hue
          const saturation = 70 + Math.random() * 30; // Saturation between 70% and 100%
          const lightness = 50 + Math.random() * 10; // Lightness between 50% and 60%
          colorsList.push(`hsl(${hue}, ${saturation}%, ${lightness}%)`);
        }
        return colorsList;
      };

      setColors(generateBrightColors(trips.length));
    }
  }, [trips]);

  const getShiftedPath = (locationSnapshots: any[] | undefined, pathIndex: number, totalPaths: number) => {
    if (!locationSnapshots) return [];
    const basePath = locationSnapshots
      .filter((snap) => snap?.lat != null && snap?.lon != null)
      .map((snap) => ({ lat: snap.lat as number, lng: snap.lon as number }));

    if (basePath.length < 2 || totalPaths <= 1) {
      return basePath;
    }

    const lane = Math.floor(pathIndex / 2) + 1;
    const direction = pathIndex % 2 === 0 ? -1 : 1;
    const offsetMeters = lane * direction * 12;

    return basePath.map((point, i) => {
      const prev = basePath[Math.max(i - 1, 0)];
      const next = basePath[Math.min(i + 1, basePath.length - 1)];

      const dx = next.lng - prev.lng;
      const dy = next.lat - prev.lat;
      const length = Math.hypot(dx, dy) || 1;

      const nx = -dy / length;
      const ny = dx / length;

      const latRad = (point.lat * Math.PI) / 180;
      const metersPerDegLat = 111320;
      const metersPerDegLng = Math.max(111320 * Math.cos(latRad), 1);

      return {
        lat: point.lat + (ny * offsetMeters) / metersPerDegLat,
        lng: point.lng + (nx * offsetMeters) / metersPerDegLng,
      };
    });
  };

  const google = window.google;

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 105px)",
          minHeight: 0,
        }}
      >
        <div style={{ flexShrink: 0 }}>
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "0.75em",
              marginBottom: isMobile ? "0.75em" : "0.5em",
            }}
          >
            <Title
              order={2}
              sx={(theme) => ({
                fontSize: theme.headings.sizes.h4.fontSize,
                fontWeight: 600,
                color: theme.colors.dark[7] || "#1A1B1E",
                letterSpacing: "-0.01em",
                lineHeight: 1.3,
              })}
            >
              Trips
            </Title>

            {!isMobile && (
              <div style={{ display: "flex", gap: "0.6rem", alignItems: "center", flexWrap: "wrap" }}>
                <DateRangePicker
                  placeholder="Pickup date"
                  aria-label="Pickup date"
                  dropdownPosition="bottom"
                  amountOfMonths={2}
                  style={{ width: "210px" }}
                  inputFormat="MM/DD/YYYY"
                  clearable={false}
                  value={dates}
                  onChange={handleDateRangeChange}
                  size="sm"
                />
                {trips.length > 0 && (
                  <Button
                    variant="outline"
                    color="cap-blue"
                    onClick={() => setMapModalOpen(true)}
                    leftIcon={<MapSearch size={16} />}
                    size="sm"
                  >
                    View on Map
                  </Button>
                )}
              </div>
            )}

            {isMobile && (
              <div style={{ width: "100%", display: "grid", gap: "0.75em" }}>
                <Button
                  variant="outline"
                  color="cap-blue"
                  fullWidth
                  rightIcon={mobileFiltersOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  onClick={() => setMobileFiltersOpen((opened) => !opened)}
                  size="sm"
                >
                  {mobileFiltersOpen ? "Hide filters" : "Show filters"}
                </Button>
                {mobileFiltersOpen && (
                  <div
                    style={{
                      display: "grid",
                      gap: "0.75em",
                      padding: "0.85em",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      backgroundColor: "#fafafa",
                    }}
                  >
                    <DateRangePicker
                      placeholder="Pickup date"
                      label="Pickup Date"
                      dropdownPosition="bottom"
                      amountOfMonths={1}
                      style={{ width: "100%" }}
                      inputFormat="MM/DD/YYYY"
                      clearable={false}
                      value={dates}
                      onChange={handleDateRangeChange}
                      size={isMobile ? "md" : "sm"}
                    />
                    <DateRangeQuickPills
                      value={dates}
                      onChange={handleDateRangeChange}
                    />
                    <Select
                      searchable
                      placeholder="Filter by status"
                      label="Status"
                      data={[
                        { value: "PLANNED", label: "Planned" },
                        { value: "ALLOCATED", label: "Allocated" },
                        { value: "PRE_INSPECTED", label: "Pre-inspected" },
                        { value: "IN_PROGRESS", label: "In Progress" },
                        { value: "ARRIVED", label: "Arrived" },
                        { value: "COMPLETED", label: "Completed" },
                      ]}
                      value={status}
                      onChange={(value) => handleFilterChange("status", value)}
                      clearable
                      size="sm"
                    />
                    <Select
                      placeholder="Filter by vehicle"
                      label="Vehicle"
                      data={vehicleOptions}
                      value={selectedVehicle}
                      onChange={(value) => handleFilterChange("vehicle", value)}
                      clearable
                      searchable
                      size="sm"
                    />
                    <Select
                      placeholder="Filter by driver"
                      label="Driver"
                      data={driverOptions}
                      value={selectedDriver}
                      onChange={(value) => handleFilterChange("driver", value)}
                      clearable
                      searchable
                      size="sm"
                    />
                    {trips.length > 0 && (
                      <Button
                        variant="outline"
                        color="cap-blue"
                        onClick={() => setMapModalOpen(true)}
                        leftIcon={<MapSearch size={16} />}
                        fullWidth
                        size="sm"
                      >
                        View on Map
                      </Button>
                    )}
                    {(status || selectedVehicle || selectedDriver) && (
                      <Button
                        variant="subtle"
                        color="gray"
                        size="sm"
                        fullWidth
                        onClick={() => {
                          handleFilterChange("status", null);
                          handleFilterChange("vehicle", null);
                          handleFilterChange("driver", null);
                        }}
                      >
                        Reset filters
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {!isMobile && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "0.6rem",
                marginBottom: "0.75rem",
                padding: "0.4rem 0.6rem",
                backgroundColor: "#f8f9fa",
                borderRadius: "6px",
                border: "1px solid #e9ecef",
              }}
            >
              <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                <Select
                  searchable
                  placeholder="Filter by status"
                  aria-label="Status"
                  data={[
                    { value: "PLANNED", label: "Planned" },
                    { value: "ALLOCATED", label: "Allocated" },
                    { value: "PRE_INSPECTED", label: "Pre-inspected" },
                    { value: "IN_PROGRESS", label: "In Progress" },
                    { value: "ARRIVED", label: "Arrived" },
                    { value: "COMPLETED", label: "Completed" },
                  ]}
                  value={status}
                  onChange={(value) => handleFilterChange("status", value)}
                  clearable
                  style={{ width: "160px" }}
                  size="sm"
                />
                <Select
                  placeholder="Filter by vehicle"
                  aria-label="Vehicle"
                  data={vehicleOptions}
                  value={selectedVehicle}
                  onChange={(value) => handleFilterChange("vehicle", value)}
                  clearable
                  searchable
                  style={{ width: "180px" }}
                  size="sm"
                />
                <Select
                  placeholder="Filter by driver"
                  aria-label="Driver"
                  data={driverOptions}
                  value={selectedDriver}
                  onChange={(value) => handleFilterChange("driver", value)}
                  clearable
                  searchable
                  style={{ width: "180px" }}
                  size="sm"
                />
                {(status || selectedVehicle || selectedDriver) && (
                  <Button
                    variant="subtle"
                    color="gray"
                    size="sm"
                    onClick={() => {
                      handleFilterChange("status", null);
                      handleFilterChange("vehicle", null);
                      handleFilterChange("driver", null);
                    }}
                  >
                    Reset filters
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "auto",
            paddingRight: "4px",
          }}
        >
          {loading && trips.length === 0 && (
            <Loader
              color="cap-navy"
              style={{ margin: "2em auto", display: "block" }}
            />
          )}
          {!loading && trips.length === 0 && doneInitialFetch && (
            <Alert
              title="No entries found"
              color="yellow"
              style={{ marginTop: "1em" }}
            >
              Please adjust your filters or select a different date range.
            </Alert>
          )}
          <TripTable
            elements={trips}
            auth={auth || null}
            companyId={companyId}
            selectedTripId={selectedTripId}
            onOpenTrip={openOverlay}
            onCloseTrip={closeOverlay}
            reloadCallback={(savedTrip) => {
              if (savedTrip) {
                updateDateRange(savedTrip.pickupDate);
              }
              reset();
            }}
          />
          <Space h="sm" />
          {trips.length > 0 && (
            <div
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexWrap: "wrap",
                gap: "1.5rem",
                marginTop: "2em",
                marginBottom: "1em",
              }}
            >
              {!paginationDone && (
                loading && trips.length > 0 ? (
                  <Loader color="cap-navy" />
                ) : (
                  <Button
                    color="cap-navy"
                    size={isMobile ? "md" : "sm"}
                    style={{
                      minWidth: isMobile ? "100%" : "160px",
                      minHeight: isMobile ? 44 : undefined,
                      height: isMobile ? 44 : undefined,
                    }}
                    onClick={() => loadMoreEntries(false)}
                    disabled={loading && trips.length > 0}
                  >
                    Load more entries
                  </Button>
                )
              )}
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  width: isMobile ? "100%" : "auto",
                  justifyContent: isMobile ? "center" : undefined,
                }}
              >
                <Text size={isMobile ? "sm" : "xs"} color="dimmed" weight={500}>
                  Page size:
                </Text>
                <SegmentedControl
                  size={isMobile ? "md" : "xs"}
                  value={String(pageSize)}
                  onChange={(val) => handlePageSizeChange(Number(val))}
                  data={[
                    { label: "20", value: "20" },
                    { label: "50", value: "50" },
                    { label: "100", value: "100" },
                  ]}
                  styles={{
                    root: {
                      minHeight: isMobile ? 44 : undefined,
                    },
                    control: {
                      minHeight: isMobile ? 44 : undefined,
                      height: isMobile ? 44 : undefined,
                    },
                    label: {
                      minHeight: isMobile ? 44 : undefined,
                      height: isMobile ? 44 : undefined,
                      display: isMobile ? "flex" : undefined,
                      alignItems: isMobile ? "center" : undefined,
                      justifyContent: isMobile ? "center" : undefined,
                      paddingLeft: 14,
                      paddingRight: 14,
                    },
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <Modal
        opened={mapModalOpen}
        onClose={() => setMapModalOpen(false)}
        title={`${trips.length} Trip${trips.length !== 1 ? "s" : ""}: ${dates[0]?.toLocaleDateString()} - ${dates[1]?.toLocaleDateString()}`}
        size="90%"
      >
        {isLoaded && (
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "80vh" }}
            center={mapCenter}
            zoom={10}
          >
            {trips.map((trip, index) => (
              <React.Fragment key={trip.id}>
                <Polyline
                  path={getShiftedPath(trip.locationSnapshots, index, trips.length)}
                  options={{
                    strokeColor: colors[index % colors.length],
                    strokeOpacity: 0.8,
                    strokeWeight: 4,
                  }}
                  onMouseOver={() => setHoveredTrip(trip)}
                  onMouseOut={() => setHoveredTrip(null)}
                />
                {trip.locationSnapshots.length > 0 && trip.locationSnapshots[0]?.lat != null && trip.locationSnapshots[0]?.lon != null && (
                  <Marker
                    position={{
                      lat: trip.locationSnapshots[0].lat,
                      lng: trip.locationSnapshots[0].lon,
                    }}
                    icon={{
                      path: google.maps.SymbolPath.CIRCLE,
                      scale: 6,
                      fillColor: "#FFF",
                      fillOpacity: 1,
                      strokeWeight: 2,
                      strokeColor: "#000",
                    }}
                    onClick={() => setSelectedMapTrip(trip)}
                  />
                )}
                {trip.locationSnapshots.length > 0 && trip.locationSnapshots[trip.locationSnapshots.length - 1]?.lat != null && trip.locationSnapshots[trip.locationSnapshots.length - 1]?.lon != null && (
                  <Marker
                    options={{ }}
                    position={{
                      lat: trip.locationSnapshots[trip.locationSnapshots.length - 1].lat,
                      lng: trip.locationSnapshots[trip.locationSnapshots.length - 1].lon,
                    }}
                    onClick={() => setSelectedMapTrip(trip)}
                  />
                )}
              </React.Fragment>
            ))}

            {hoveredTrip && (
              <InfoWindow
                options={{ pixelOffset: new window.google.maps.Size(0, -30), headerDisabled: true }}
                position={{
                  lat: hoveredTrip.locationSnapshots[hoveredTrip.locationSnapshots.length - 1].lat,
                  lng: hoveredTrip.locationSnapshots[hoveredTrip.locationSnapshots.length - 1].lon,
                }}
                onCloseClick={() => setHoveredTrip(null)}
              >
                <div>
                  <h4>{hoveredTrip.description}</h4>
                  <p><b>Pickup:</b> {hoveredTrip.pickupAddress}</p>
                  <p><b>Delivery:</b> {hoveredTrip.deliveryAddress}</p>
                  <p><b>Status:</b> {hoveredTrip.status}</p>
                  <p><b>Vehicle:</b> {hoveredTrip.fleetDisplayName || "N/A"}</p>
                  <p><b>Driver:</b> {hoveredTrip.fleetOperatorNameAndSurname || "N/A"}</p>
                  <Divider />
                  <p><b>Start:</b> { new Date(hoveredTrip.locationSnapshots[0].timestamp).toLocaleString()}</p>
                  <p><b>End:</b> { new Date(hoveredTrip.locationSnapshots[hoveredTrip.locationSnapshots.length - 1].timestamp).toLocaleString()}</p>
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        )}
      </Modal>

      <Modal
        opened={!!selectedMapTrip}
        onClose={() => setSelectedMapTrip(null)}
        title={selectedMapTrip?.description || "Trip details"}
        size="lg"
      >
        {selectedMapTrip && <TripTimeline trip={selectedMapTrip} key={selectedMapTrip.id} />}
      </Modal>
    </>
  );
}

export default Trips;